/**
 * A record id with a copy button.
 *
 * Exists because these ids get pasted into other systems — a frontend that
 * fetches a site by id, a support ticket, a query. Selecting a monospace
 * ULID by hand is fiddly and easy to get subtly wrong (one missing
 * character produces a valid-looking id that resolves to nothing), so the
 * copy is a button rather than a select-and-drag.
 */

import { useCallback, useEffect, useState } from "react"
import { cn } from "@/lib/utils"
import { Check, Copy } from "lucide-react"

export interface CopyableIdProps {
  id: string
  /** Prefixed label, e.g. "Site ID". Omit for a bare id. */
  label?: string
  className?: string
}

export function CopyableId({ id, label, className }: CopyableIdProps) {
  const [copied, setCopied] = useState(false)

  // Reset the tick so the button does not sit in a permanent success state.
  useEffect(() => {
    if (!copied) return
    const t = setTimeout(() => setCopied(false), 1500)
    return () => clearTimeout(t)
  }, [copied])

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(id)
      setCopied(true)
    } catch {
      /*
       * Clipboard access can be refused (insecure context, denied
       * permission). The id is selectable text either way, so failing
       * silently leaves the user no worse off than without the button.
       */
    }
  }, [id])

  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? "Copied" : `Copy ${label ?? "id"}`}
      className={cn(
        "group inline-flex max-w-full items-center gap-1.5 rounded text-[11px] text-muted-foreground",
        "transition-colors hover:text-foreground",
        className,
      )}
    >
      {label && <span className="shrink-0">{label}</span>}
      {/* select-all so a click-drag still grabs the whole id if preferred. */}
      <code className="select-all truncate font-mono">{id}</code>
      {copied
        ? <Check className="h-3 w-3 shrink-0 text-emerald-600 dark:text-emerald-500" />
        : <Copy className="h-3 w-3 shrink-0 opacity-50 group-hover:opacity-100" />}
    </button>
  )
}
